import type { Transaction } from '../types';
import { isCreditWithdrawal } from './withdrawalTypeDisplay';
import { getWithdrawalStatusDisplay } from './withdrawalStatusDisplay';

export type TransactionDisplayTypeKey =
  | 'admin_credit_sale'
  | 'admin_credit_buyback'
  | 'shop_counter_deposit'
  | 'shop_counter_redeem'
  | 'agent_cash_deposit'
  | 'agent_cash_withdrawal'
  | 'super_agent_cash_deposit'
  | 'super_agent_cash_withdrawal'
  | 'balance_conversion'
  | 'online_deposit_crypto'
  | 'online_deposit_fiat'
  | 'credit_purchase'
  | 'usd_withdrawal'
  | 'credit_withdrawal'
  | 'deposit'
  | 'withdrawal';

export type TransactionDisplayType = {
  key: TransactionDisplayTypeKey;
  label: string;
  badgeClassName: string;
};

export type TransactionAmountDisplay =
  | { mode: 'usd'; signed: boolean; amount: number; currency: string }
  | {
      mode: 'split_cash_credits';
      cashPaid?: number;
      cashReceived?: number;
      credits: number;
      currency: string;
      creditDirection: 'in' | 'out';
    }
  | { mode: 'credits_only'; credits: number; direction: 'in' | 'out' };

export type TransactionDisplayInput = Pick<Transaction, 'type'> &
  Partial<
    Pick<
      Transaction,
      | 'provider'
      | 'buyerType'
      | 'amount'
      | 'currency'
      | 'metadata'
      | 'syncMetadata'
      | 'paymentMethod'
      | 'callbackData'
      | 'fundingSummary'
      | 'paymentResult'
    >
  >;

const CRYPTO_PROVIDERS = new Set(['btcpay', 'zeroxprocessing']);
const FIAT_PROVIDERS = new Set(['linkmepay', 'meld', 'ggusonepay']);
const ORG_BUYER_TYPES = new Set(['agent', 'shop', 'super_agent']);
const CASH_PROVIDERS = new Set([
  'admin_cash',
  'shop_player_cash',
  'agent_cash',
  'super_agent_cash',
]);

const TYPE_BADGE: Record<TransactionDisplayTypeKey, string> = {
  admin_credit_sale: 'bg-violet-100 text-violet-800',
  admin_credit_buyback: 'bg-violet-100 text-violet-800',
  shop_counter_deposit: 'bg-emerald-100 text-emerald-800',
  shop_counter_redeem: 'bg-emerald-100 text-emerald-800',
  agent_cash_deposit: 'bg-amber-100 text-amber-800',
  agent_cash_withdrawal: 'bg-amber-100 text-amber-800',
  super_agent_cash_deposit: 'bg-indigo-100 text-indigo-800',
  super_agent_cash_withdrawal: 'bg-indigo-100 text-indigo-800',
  balance_conversion: 'bg-sky-100 text-sky-800',
  online_deposit_crypto: 'bg-blue-100 text-blue-800',
  online_deposit_fiat: 'bg-blue-100 text-blue-800',
  credit_purchase: 'bg-blue-100 text-blue-800',
  usd_withdrawal: 'bg-orange-100 text-orange-800',
  credit_withdrawal: 'bg-emerald-100 text-emerald-800',
  deposit: 'bg-blue-100 text-blue-800',
  withdrawal: 'bg-orange-100 text-orange-800',
};

function providerKey(transaction: TransactionDisplayInput): string {
  return String(transaction.provider || '').toLowerCase();
}

function metaFlag(transaction: TransactionDisplayInput, key: string): boolean {
  const meta = transaction.metadata ?? {};
  const sync = transaction.syncMetadata ?? {};
  return meta[key] === true || sync[key] === true;
}

/** 0xProcessing CDN static wallet — USD wallet settlement (amount is USD; currency is payment coin label). */
export function isZeroxStaticWalletUsdSettlement(transaction: TransactionDisplayInput): boolean {
  if (providerKey(transaction) !== 'zeroxprocessing') return false;
  if (transaction.type !== 'deposit') return false;
  const meta = transaction.metadata ?? {};
  if (meta.staticWallet === true) return true;
  if (String(meta.staticWallet ?? '').toLowerCase() === 'true') return true;
  if (String(transaction.paymentMethod ?? '').toLowerCase() === 'zeroxprocessing_static_wallet') {
    return true;
  }
  if (meta.settlementMode === 'usd_only') return true;
  return false;
}

/** 0xProcessing deposit where amount is USD but currency field is the payment coin label. */
export function isZeroxProcessingUsdAmountRow(transaction: TransactionDisplayInput): boolean {
  if (providerKey(transaction) !== 'zeroxprocessing') return false;
  if (transaction.type !== 'deposit') return false;
  return isNonUsdCoinLabel(String(transaction.currency || 'USD'));
}

/** 0xProcessing withdrawal where amount is USD but currency field is the payout coin label. */
export function isZeroxProcessingUsdAmountWithdrawal(transaction: TransactionDisplayInput): boolean {
  if (providerKey(transaction) !== 'zeroxprocessing') return false;
  if (transaction.type !== 'withdrawal') return false;
  return isNonUsdCoinLabel(String(transaction.currency || 'USD'));
}

function isNonUsdCoinLabel(currency: string): boolean {
  const label = currency.trim();
  return Boolean(label) && label.toUpperCase() !== 'USD';
}

function pickZeroxCoinLabel(transaction: TransactionDisplayInput): string {
  return String(transaction.currency || '').trim();
}

function pickCallbackData(transaction: TransactionDisplayInput): Record<string, unknown> {
  const cb = transaction.callbackData;
  return cb && typeof cb === 'object' ? cb : {};
}

function normalizeZeroxCryptoPayment(
  rawAmount: unknown,
  rawCurrency: unknown,
  fallbackCurrency: string,
): { amount: number; currency: string } | null {
  const amount = Number(rawAmount);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  const currency = String(rawCurrency ?? fallbackCurrency).trim();
  if (!isNonUsdCoinLabel(currency)) return null;

  return { amount, currency };
}

/** Crypto paid/received from 0x webhook callback payload. */
function pickZeroxCryptoFromCallbackData(
  transaction: TransactionDisplayInput,
): { amount: number; currency: string } | null {
  const cb = pickCallbackData(transaction);
  if (Object.keys(cb).length === 0) return null;

  const raw = cb.TotalAmount ?? cb.totalAmount ?? cb.Amount ?? cb.amount;
  return normalizeZeroxCryptoPayment(raw, cb.Currency ?? cb.currency, pickZeroxCoinLabel(transaction));
}

/** Crypto from persisted metadata (e.g. player withdrawal payout amount). */
function pickZeroxCryptoFromMetadata(
  transaction: TransactionDisplayInput,
): { amount: number; currency: string } | null {
  const metadata = transaction.metadata ?? {};
  const zerox = metadata.zerox;
  const zeroxRecord =
    zerox && typeof zerox === 'object' ? (zerox as Record<string, unknown>) : null;

  const rawAmount =
    zeroxRecord?.cryptoAmount ??
    zeroxRecord?.crypto_amount ??
    metadata.cryptoAmount ??
    metadata.crypto_amount;
  const rawCurrency =
    zeroxRecord?.currency ??
    zeroxRecord?.outCurrency ??
    metadata.outCurrency ??
    metadata.out_currency;

  return normalizeZeroxCryptoPayment(rawAmount, rawCurrency, pickZeroxCoinLabel(transaction));
}

/** Crypto from stored provider response when metadata/callback are absent. */
function pickZeroxCryptoFromPaymentResult(
  transaction: TransactionDisplayInput,
): { amount: number; currency: string } | null {
  const paymentResult = transaction.paymentResult;
  if (!paymentResult || typeof paymentResult !== 'object') return null;

  const record = paymentResult as Record<string, unknown>;
  const responseData =
    record.responseData && typeof record.responseData === 'object'
      ? (record.responseData as Record<string, unknown>)
      : record.data && typeof record.data === 'object'
        ? (record.data as Record<string, unknown>)
        : record;

  const rawAmount = responseData.amount ?? responseData.Amount ?? record.amount ?? record.Amount;
  const rawCurrency =
    responseData.currency ??
    responseData.Currency ??
    record.currency ??
    record.Currency;

  return normalizeZeroxCryptoPayment(rawAmount, rawCurrency, pickZeroxCoinLabel(transaction));
}

/** Row eligible for 0x USD primary + crypto sub-line. */
export function isZeroxDualDisplayRow(transaction: TransactionDisplayInput): boolean {
  if (providerKey(transaction) !== 'zeroxprocessing') return false;
  if (transaction.type === 'deposit') {
    if (isZeroxStaticWalletUsdSettlement(transaction)) return true;
    return isZeroxProcessingUsdAmountRow(transaction);
  }
  if (transaction.type === 'withdrawal') {
    return isZeroxProcessingUsdAmountWithdrawal(transaction);
  }
  return false;
}

/**
 * Crypto actually paid/sent for 0xProcessing (not the USD amount on the txn row).
 * Fallback order: callbackData → metadata.zerox.cryptoAmount → paymentResult.
 */
export function pickZeroxCryptoPayment(
  transaction: TransactionDisplayInput,
): { amount: number; currency: string } | null {
  if (!isZeroxDualDisplayRow(transaction)) return null;

  return (
    pickZeroxCryptoFromCallbackData(transaction) ??
    pickZeroxCryptoFromMetadata(transaction) ??
    pickZeroxCryptoFromPaymentResult(transaction)
  );
}

/** @deprecated Use pickZeroxCryptoPayment */
export const pickZeroxStaticWalletCryptoPayment = pickZeroxCryptoPayment;

export function formatCryptoPaymentAmount(amount: number, currency: string): string {
  const code = currency.trim();
  const upper = code.toUpperCase();
  const isStableLike = upper.includes('USDT') || upper.includes('USDC');
  const maxFraction = isStableLike ? 4 : 8;
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFraction,
  });
  return `${formatted} ${code}`;
}

export function resolveZeroxDualDisplay(transaction: TransactionDisplayInput): {
  usdAmount: number;
  cryptoLabel: string | null;
} | null {
  if (!isZeroxDualDisplayRow(transaction)) return null;

  const funding = pickFundingFields(transaction);
  const usdAmount = funding.usdAmount ?? Math.abs(Number(transaction.amount || 0));
  if (!Number.isFinite(usdAmount) || usdAmount <= 0) return null;

  const crypto = pickZeroxCryptoPayment(transaction);

  return {
    usdAmount,
    cryptoLabel: crypto ? formatCryptoPaymentAmount(crypto.amount, crypto.currency) : null,
  };
}

/** @deprecated Use resolveZeroxDualDisplay */
export const resolveZeroxStaticWalletDualDisplay = resolveZeroxDualDisplay;

function buildZeroxUsdListColumns(
  transaction: TransactionDisplayInput,
  dual: { usdAmount: number; cryptoLabel: string | null },
): Pick<
  TransactionListAmountColumns,
  'usdLabel' | 'usdTone' | 'usdDirection' | 'usdUnit' | 'usdSubLabel'
> {
  const direction: MoneyFlowDirection = transaction.type === 'withdrawal' ? 'out' : 'in';
  return {
    usdLabel: formatCashAmount(dual.usdAmount, 'USD'),
    usdTone: direction === 'in' ? 'positive' : 'negative',
    usdDirection: direction,
    usdUnit: null,
    usdSubLabel: dual.cryptoLabel,
  };
}

export function pickFundingFields(transaction: TransactionDisplayInput): {
  creditAmount: number | null;
  cashCharged: number | null;
  cashWithdrawn: number | null;
  usdAmount: number | null;
  balanceToCredits: boolean;
} {
  const metadata = transaction.metadata ?? {};
  const syncMetadata = transaction.syncMetadata ?? {};
  const creditRaw = metadata.creditAmount ?? metadata.credit_amount;
  const cashChargedRaw = metadata.cashCharged ?? metadata.cash_charged;
  const cashWithdrawnRaw = metadata.cashWithdrawn ?? metadata.cash_withdrawn;
  const usdRaw =
    metadata.usdAmount ??
    metadata.usd_amount ??
    transaction.fundingSummary?.usdAmount;

  const creditAmount = creditRaw != null ? Number(creditRaw) : null;
  const cashCharged = cashChargedRaw != null ? Number(cashChargedRaw) : null;
  const cashWithdrawn = cashWithdrawnRaw != null ? Number(cashWithdrawnRaw) : null;
  const usdFromMeta = usdRaw != null ? Number(usdRaw) : null;
  const usdFromTxn = transaction.amount != null ? Number(transaction.amount) : null;

  const balanceToCredits =
    metadata.balanceToCredits === true ||
    syncMetadata.balanceToCredits === true ||
    providerKey(transaction) === 'balance_conversion';

  return {
    creditAmount: Number.isFinite(creditAmount) ? creditAmount : null,
    cashCharged: Number.isFinite(cashCharged) ? cashCharged : null,
    cashWithdrawn: Number.isFinite(cashWithdrawn) ? cashWithdrawn : null,
    usdAmount: Number.isFinite(usdFromMeta)
      ? usdFromMeta
      : Number.isFinite(usdFromTxn)
        ? usdFromTxn
        : null,
    balanceToCredits,
  };
}

export function getTransactionDisplayType(transaction: TransactionDisplayInput): TransactionDisplayType {
  const type = transaction.type;
  const provider = providerKey(transaction);
  const buyerType = String(transaction.buyerType || '').toLowerCase();

  if (type === 'deposit' && provider === 'admin_cash' && metaFlag(transaction, 'adminCashFunding')) {
    return { key: 'admin_credit_sale', label: 'Admin Credit Sale', badgeClassName: TYPE_BADGE.admin_credit_sale };
  }
  if (type === 'withdrawal' && provider === 'admin_cash' && metaFlag(transaction, 'adminCashWithdrawal')) {
    return {
      key: 'admin_credit_buyback',
      label: 'Admin Credit Buyback',
      badgeClassName: TYPE_BADGE.admin_credit_buyback,
    };
  }
  if (type === 'deposit' && provider === 'shop_player_cash') {
    return {
      key: 'shop_counter_deposit',
      label: 'Shop Counter Deposit',
      badgeClassName: TYPE_BADGE.shop_counter_deposit,
    };
  }
  if (type === 'withdrawal' && provider === 'shop_player_cash') {
    return {
      key: 'shop_counter_redeem',
      label: 'Shop Counter Redeem',
      badgeClassName: TYPE_BADGE.shop_counter_redeem,
    };
  }
  if (type === 'deposit' && provider === 'agent_cash') {
    return {
      key: 'agent_cash_deposit',
      label: 'Agent Cash Deposit',
      badgeClassName: TYPE_BADGE.agent_cash_deposit,
    };
  }
  if (type === 'withdrawal' && provider === 'agent_cash') {
    return {
      key: 'agent_cash_withdrawal',
      label: 'Agent Cash Withdrawal',
      badgeClassName: TYPE_BADGE.agent_cash_withdrawal,
    };
  }
  if (type === 'deposit' && provider === 'super_agent_cash') {
    return {
      key: 'super_agent_cash_deposit',
      label: 'Super Agent Cash Deposit',
      badgeClassName: TYPE_BADGE.super_agent_cash_deposit,
    };
  }
  if (type === 'withdrawal' && provider === 'super_agent_cash') {
    return {
      key: 'super_agent_cash_withdrawal',
      label: 'Super Agent Cash Withdrawal',
      badgeClassName: TYPE_BADGE.super_agent_cash_withdrawal,
    };
  }
  if (provider === 'balance_conversion' || pickFundingFields(transaction).balanceToCredits) {
    return {
      key: 'balance_conversion',
      label: 'Balance Conversion',
      badgeClassName: TYPE_BADGE.balance_conversion,
    };
  }
  if (type === 'deposit' && CRYPTO_PROVIDERS.has(provider)) {
    return {
      key: 'online_deposit_crypto',
      label: 'Online Deposit',
      badgeClassName: TYPE_BADGE.online_deposit_crypto,
    };
  }
  if (type === 'deposit' && FIAT_PROVIDERS.has(provider)) {
    return {
      key: 'online_deposit_fiat',
      label: 'USD wallet deposit',
      badgeClassName: TYPE_BADGE.online_deposit_fiat,
    };
  }
  if (type === 'deposit' && ORG_BUYER_TYPES.has(buyerType) && provider && !CASH_PROVIDERS.has(provider)) {
    return {
      key: 'credit_purchase',
      label: 'Credit Purchase',
      badgeClassName: TYPE_BADGE.credit_purchase,
    };
  }
  if (
    type === 'withdrawal' &&
    (provider === 'portal_usd_withdrawal' || FIAT_PROVIDERS.has(provider) || CRYPTO_PROVIDERS.has(provider))
  ) {
    return {
      key: 'usd_withdrawal',
      label: 'USD Withdrawal',
      badgeClassName: TYPE_BADGE.usd_withdrawal,
    };
  }
  if (
    type === 'withdrawal' &&
    isCreditWithdrawal({
      withdrawalType: transaction.paymentMethod ?? null,
      provider: transaction.provider,
      metadata: transaction.metadata ?? null,
    })
  ) {
    return {
      key: 'credit_withdrawal',
      label: 'Credit Withdrawal',
      badgeClassName: TYPE_BADGE.credit_withdrawal,
    };
  }

  if (type === 'deposit' && provider === 'admin_cash') {
    return {
      key: 'admin_credit_sale',
      label: 'Admin Credit Sale',
      badgeClassName: TYPE_BADGE.admin_credit_sale,
    };
  }

  if (type === 'withdrawal') {
    return { key: 'withdrawal', label: 'Withdrawal', badgeClassName: TYPE_BADGE.withdrawal };
  }
  return { key: 'deposit', label: 'Deposit', badgeClassName: TYPE_BADGE.deposit };
}

export function getTransactionAmountDisplay(
  transaction: TransactionDisplayInput,
  displayTypeKey?: TransactionDisplayTypeKey,
): TransactionAmountDisplay {
  const key = displayTypeKey ?? getTransactionDisplayType(transaction).key;
  const currency = (transaction.currency || 'USD').toUpperCase();
  const funding = pickFundingFields(transaction);
  const txnAmount = Math.abs(Number(transaction.amount || 0));
  const isWithdrawal = transaction.type === 'withdrawal';

  if (key === 'admin_credit_sale') {
    return {
      mode: 'split_cash_credits',
      cashPaid: funding.cashCharged ?? txnAmount,
      credits: funding.creditAmount ?? 0,
      currency,
      creditDirection: 'in',
    };
  }

  if (key === 'admin_credit_buyback') {
    return {
      mode: 'split_cash_credits',
      cashReceived: funding.cashWithdrawn ?? txnAmount,
      credits: funding.creditAmount ?? 0,
      currency,
      creditDirection: 'out',
    };
  }

  if (
    key === 'agent_cash_deposit' ||
    key === 'super_agent_cash_deposit' ||
    key === 'credit_purchase' ||
    key === 'balance_conversion' ||
    key === 'shop_counter_deposit'
  ) {
    const cash = funding.cashCharged ?? funding.usdAmount ?? txnAmount;
    const credits = funding.creditAmount;
    if (credits != null && Number.isFinite(credits)) {
      return {
        mode: 'split_cash_credits',
        cashPaid: cash,
        credits,
        currency,
        creditDirection: 'in',
      };
    }
  }

  if (
    key === 'agent_cash_withdrawal' ||
    key === 'super_agent_cash_withdrawal' ||
    key === 'shop_counter_redeem'
  ) {
    const cash = funding.cashWithdrawn ?? funding.usdAmount ?? txnAmount;
    const credits = funding.creditAmount;
    if (credits != null && Number.isFinite(credits)) {
      return {
        mode: 'split_cash_credits',
        cashReceived: cash,
        credits,
        currency,
        creditDirection: 'out',
      };
    }
  }

  if (key === 'credit_withdrawal') {
    const credits = funding.creditAmount ?? txnAmount;
    return {
      mode: 'credits_only',
      credits,
      direction: 'out',
    };
  }

  if (key === 'usd_withdrawal') {
    return {
      mode: 'usd',
      signed: false,
      amount: txnAmount,
      currency: 'USD',
    };
  }

  return {
    mode: 'usd',
    signed: !isWithdrawal,
    amount: txnAmount,
    currency,
  };
}

export type MoneyFlowDirection = 'in' | 'out';

export type TransactionListAmountColumns = {
  usdLabel: string | null;
  usdTone: 'positive' | 'negative' | 'neutral';
  /** Cash/USD flow relative to the platform (in = received, out = paid out). */
  usdDirection: MoneyFlowDirection | null;
  usdUnit: string | null;
  /** Crypto paid (0xProcessing): rendered below USD primary line. */
  usdSubLabel: string | null;
  creditLabel: string | null;
  creditTone: 'positive' | 'negative' | 'neutral';
  creditDirection: MoneyFlowDirection | null;
  creditUnit: string | null;
};

const EMPTY_CREDIT_COLUMNS = {
  creditLabel: null,
  creditTone: 'neutral' as const,
  creditDirection: null,
  creditUnit: null,
};

/** Separate USD and credit values for payment transaction list columns. */
export function getTransactionListAmountColumns(
  transaction: TransactionDisplayInput,
): TransactionListAmountColumns {
  const display = getTransactionAmountDisplay(transaction);
  const zeroxDual = resolveZeroxDualDisplay(transaction);

  if (zeroxDual) {
    const usdCols = buildZeroxUsdListColumns(transaction, zeroxDual);
    if (display.mode === 'split_cash_credits') {
      const creditDirection: MoneyFlowDirection =
        display.creditDirection === 'in' ? 'in' : 'out';
      return {
        ...usdCols,
        creditLabel: formatCreditsAmount(display.credits),
        creditTone: creditDirection === 'in' ? 'positive' : 'negative',
        creditDirection,
        creditUnit: 'Credits',
      };
    }
    return { ...usdCols, ...EMPTY_CREDIT_COLUMNS };
  }

  if (display.mode === 'usd') {
    const direction: MoneyFlowDirection = display.signed ? 'in' : 'out';
    return {
      usdLabel: formatCashAmount(display.amount, display.currency),
      usdTone: direction === 'in' ? 'positive' : 'negative',
      usdDirection: direction,
      usdUnit: null,
      usdSubLabel: null,
      ...EMPTY_CREDIT_COLUMNS,
    };
  }

  if (display.mode === 'credits_only') {
    const direction: MoneyFlowDirection = display.direction === 'in' ? 'in' : 'out';
    return {
      usdLabel: null,
      usdTone: 'neutral',
      usdDirection: null,
      usdUnit: null,
      usdSubLabel: null,
      creditLabel: formatCreditsAmount(display.credits),
      creditTone: direction === 'in' ? 'positive' : 'negative',
      creditDirection: direction,
      creditUnit: 'Credits',
    };
  }

  const cashValue =
    display.cashPaid != null
      ? display.cashPaid
      : display.cashReceived != null
        ? display.cashReceived
        : null;
  const usdDirection: MoneyFlowDirection | null =
    display.cashPaid != null ? 'in' : display.cashReceived != null ? 'out' : null;
  const creditDirection: MoneyFlowDirection =
    display.creditDirection === 'in' ? 'in' : 'out';

  return {
    usdLabel: cashValue != null ? formatCashAmount(cashValue, display.currency) : null,
    // Dual-column cash side: color by flow, keep amount unsigned (In/Out badge carries meaning).
    usdTone: usdDirection === 'in' ? 'positive' : usdDirection === 'out' ? 'negative' : 'neutral',
    usdDirection,
    usdUnit: null,
    usdSubLabel: null,
    creditLabel: formatCreditsAmount(display.credits),
    creditTone: creditDirection === 'in' ? 'positive' : 'negative',
    creditDirection,
    creditUnit: 'Credits',
  };
}

export function getOverallStatusDisplay(status: string | null | undefined) {
  return getWithdrawalStatusDisplay(status);
}

/** List badge: prefer fulfillment status for multi-game deposits when backend provides it. */
export function getTransactionListStatusDisplay(
  transaction: Pick<Transaction, 'status' | 'fulfillmentStatus'>,
): ReturnType<typeof getWithdrawalStatusDisplay> {
  const raw = transaction.fulfillmentStatus || transaction.status;
  return getOverallStatusDisplay(raw);
}

/** Expanded child allocation row — refunded allocations failed first, then money was returned. */
export function getAllocationChildStatusDisplay(
  status: string | null | undefined,
): ReturnType<typeof getWithdrawalStatusDisplay> {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'refunded') {
    return {
      status: 'refunded',
      label: 'Failed - Refunded',
      className: 'bg-orange-100 text-orange-900',
      tone: 'warning',
    };
  }
  return getOverallStatusDisplay(status);
}

export function formatTimelineOutcome(status: string | null | undefined): string {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'success' || normalized === 'completed' || normalized === 'approved') {
    return 'Successful';
  }
  if (normalized === 'failed' || normalized === 'rejected' || normalized === 'cancelled') {
    return 'Failed';
  }
  if (normalized === 'pending' || normalized === 'expired' || normalized === 'processing') {
    return 'Pending';
  }
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function formatCreditsAmount(credits: number): string {
  return credits.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function formatCashAmount(amount: number, currency = 'USD'): string {
  const code = currency.toUpperCase();
  if (code === 'USD') {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code}`;
}

export function formatTransactionAmountForExport(
  transaction: TransactionDisplayInput,
): { amountDisplay: string; cashAmount: string; credits: string } {
  const zeroxDual = resolveZeroxDualDisplay(transaction);
  if (zeroxDual) {
    const sign = transaction.type === 'withdrawal' ? '-' : '+';
    const usdPart = `${sign}${formatCashAmount(zeroxDual.usdAmount, 'USD')}`;
    const line = zeroxDual.cryptoLabel
      ? `${usdPart} / ${zeroxDual.cryptoLabel}`
      : usdPart;
    return { amountDisplay: line, cashAmount: line, credits: '' };
  }

  const displayType = getTransactionDisplayType(transaction);
  const amount = getTransactionAmountDisplay(transaction, displayType.key);
  const currency = transaction.currency || 'USD';

  if (amount.mode === 'usd') {
    const sign = amount.signed ? '+' : '-';
    const line = `${sign}${formatCashAmount(amount.amount, amount.currency)}`;
    return { amountDisplay: line, cashAmount: line, credits: '' };
  }

  if (amount.mode === 'credits_only') {
    const sign = amount.direction === 'in' ? '+' : '-';
    const creditsLine = `${sign}${formatCreditsAmount(amount.credits)} credits`;
    return { amountDisplay: creditsLine, cashAmount: '', credits: creditsLine };
  }

  const cashLine = amount.cashPaid != null
    ? `${formatCashAmount(amount.cashPaid, currency)} paid`
    : amount.cashReceived != null
      ? `${formatCashAmount(amount.cashReceived, currency)} received`
      : '';
  const creditSign = amount.creditDirection === 'in' ? '+' : '-';
  const creditsLine = `${creditSign}${formatCreditsAmount(amount.credits)} credits`;
  return {
    amountDisplay: [cashLine, creditsLine].filter(Boolean).join('; '),
    cashAmount: cashLine,
    credits: creditsLine,
  };
}
